/**
 * Nearby Stores Section JavaScript
 * Handles geolocation, distance calculation, and store interactions
 */

class NearbyStores {
  constructor(container) {
    this.container = container;
    this.maxStores = parseInt(container.dataset.maxStores) || 10;
    this.enableGeolocation = container.dataset.enableGeolocation === "true";
    this.distanceUnit = container.dataset.distanceUnit || "km";
    this.useShopifyLocations = container.dataset.useShopifyLocations === "true";
    this.colorScheme = container.dataset.colorScheme || "";
    this.cardsDesktop = parseInt(container.dataset.cardsDesktop) || 4;
    this.cardsMobile = parseInt(container.dataset.cardsMobile) || 1;
    this.userLocation = null;
    this.storeCards = [];

    // Caching properties
    this.distanceCache = new Map();
    this.locationCacheKey = null;
    this.lastCalculationTime = null;
    this.cacheExpiryTime = 5 * 60 * 1000; // 5 minutes
    this.isCalculating = false;
    this.pendingSort = false;

    // No error element needed
    this.storesList = container.querySelector(".nearby-stores-list");

    this.init();
  }

  init() {
    // Fast initialization with deferred heavy operations
    this.collectStoreCards();
    this.bindEvents();
    this.setupSmoothScrolling();
    this.setupResponsiveCards();

    // If using Shopify locations and no coordinates found, try to geocode addresses
    if (this.useShopifyLocations && this.storeCards.length === 0) {
      console.warn(
        "No Shopify locations found with coordinates. Make sure your store locations have latitude/longitude set in Shopify admin."
      );
    }

    // No loading element to manage

    // Defer geolocation to next frame for faster initial render
    if (this.enableGeolocation && this.storeCards.length > 0) {
      requestAnimationFrame(() => {
        this.requestGeolocation();
      });
    } else {
      // Defer display to allow DOM to settle - no loading needed if no geolocation
      requestAnimationFrame(() => {
        this.displayStores();
      });
    }
  }

  collectStoreCards() {
    const cards = this.storesList.querySelectorAll(".store-card");
    this.storeCards = Array.from(cards)
      .map((card) => ({
        element: card,
        name: card.dataset.storeName,
        latitude: parseFloat(card.dataset.latitude),
        longitude: parseFloat(card.dataset.longitude),
        distance: null,
        isShopifyLocation: card.dataset.locationId ? true : false,
        locationId: card.dataset.locationId || card.dataset.blockId,
      }))
      .filter((store) => !isNaN(store.latitude) && !isNaN(store.longitude));

    console.log(
      `Found ${this.storeCards.length} stores (Shopify locations: ${this.useShopifyLocations})`
    );
  }

  bindEvents() {
    // Bind direction button clicks
    this.storesList.addEventListener("click", (e) => {
      const directionsBtn = e.target.closest(".store-card__directions-btn");
      if (directionsBtn) {
        // Check if it's a link (custom URL) or button (address search)
        if (directionsBtn.tagName === "A") {
          // Custom URL - let the link handle it naturally, don't prevent default
          return true;
        } else {
          // Address search - prevent default and handle programmatically
          e.preventDefault();
          this.openDirections(directionsBtn.dataset.address);
        }
      }
    });

    // Bind store card clicks for directions
    this.storesList.addEventListener("click", (e) => {
      const storeCard = e.target.closest(".store-card");
      if (
        storeCard &&
        !e.target.closest(
          ".store-card__phone-link, .store-card__directions-btn"
        )
      ) {
        const directionsBtn = storeCard.querySelector(
          ".store-card__directions-btn"
        );
        if (directionsBtn) {
          this.openDirections(directionsBtn.dataset.address);
        }
      }
    });

    // Keyboard navigation
    this.storesList.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        const storeCard = e.target.closest(".store-card");
        if (storeCard) {
          e.preventDefault();
          const directionsBtn = storeCard.querySelector(
            ".store-card__directions-btn"
          );
          if (directionsBtn) {
            this.openDirections(directionsBtn.dataset.address);
          }
        }
      }
    });
  }

  requestGeolocation() {
    if (!navigator.geolocation) {
      this.handleGeolocationError("Geolocation not supported");
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000, // 5 minutes
    };

    navigator.geolocation.getCurrentPosition(
      (position) => this.handleGeolocationSuccess(position),
      (error) => this.handleGeolocationError(error),
      options
    );
  }

  handleGeolocationSuccess(position) {
    this.userLocation = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
    };

    console.log("User location:", this.userLocation);

    // Calculate distances immediately
    this.calculateDistancesSync();
    this.sortStoresByDistance();
    this.displayStores();
    // No loading to hide
  }

  handleGeolocationError(error) {
    console.warn("Geolocation error:", error);
    this.displayStores(); // Show stores without distance sorting
  }

  calculateDistancesSync() {
    if (!this.userLocation) return;

    console.log("Calculating distances for", this.storeCards.length, "stores");

    this.storeCards.forEach((store) => {
      if (!isNaN(store.latitude) && !isNaN(store.longitude)) {
        store.distance = this.calculateHaversineDistance(
          this.userLocation.latitude,
          this.userLocation.longitude,
          store.latitude,
          store.longitude
        );
        console.log(
          `Store ${store.name}: ${store.distance.toFixed(2)} ${
            this.distanceUnit
          }`
        );
      }
    });
  }

  calculateHaversineDistance(lat1, lon1, lat2, lon2) {
    const R = this.distanceUnit === "miles" ? 3959 : 6371; // Earth's radius
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  sortStoresByDistance() {
    if (this.isCalculating) {
      this.pendingSort = true;
      return;
    }

    if (!this.userLocation) {
      // Sort alphabetically by name if no location
      this.storeCards.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      // Sort by distance
      this.storeCards.sort(
        (a, b) => (a.distance || Infinity) - (b.distance || Infinity)
      );
    }
  }

  displayStores() {
    // Sort stores first
    this.sortStoresByDistance();

    // Limit to max stores
    const storesToShow = this.storeCards.slice(0, this.maxStores);

    // Clear the list first
    this.storesList.innerHTML = "";

    // Add stores in sorted order
    storesToShow.forEach((store) => {
      // Update distance display if available
      if (store.distance !== null && store.distance !== undefined) {
        this.updateDistanceDisplay(store.element, store.distance);
      }
      this.storesList.appendChild(store.element);
    });

    // Hide any remaining stores that exceed maxStores
    this.storeCards.slice(this.maxStores).forEach((store) => {
      if (store.element.parentNode) {
        store.element.parentNode.removeChild(store.element);
      }
    });

    // Update card width and scroll position after DOM updates
    setTimeout(() => {
      this.updateCardWidth();
      this.optimizeScrollPosition();
    }, 100);
  }

  updateDistanceDisplay(cardElement, distance) {
    const distanceContainer = cardElement.querySelector(
      "[data-distance-container]"
    );
    const distanceValue = cardElement.querySelector("[data-distance-value]");

    if (
      distanceContainer &&
      distanceValue &&
      distance !== null &&
      distance !== undefined
    ) {
      let displayDistance;
      let unit = this.distanceUnit;

      if (this.distanceUnit === "km") {
        if (distance < 1) {
          displayDistance = Math.round(distance * 1000);
          unit = "m";
        } else {
          displayDistance = distance.toFixed(1);
          unit = "km";
        }
      } else {
        if (distance < 1) {
          displayDistance = Math.round(distance * 5280);
          unit = "ft";
        } else {
          displayDistance = distance.toFixed(1);
          unit = "mi";
        }
      }

      distanceValue.textContent = displayDistance;
      distanceContainer.querySelector(
        ".store-card__distance-unit"
      ).textContent = unit;
      distanceContainer.style.display = "flex";
    }
  }

  openDirections(address) {
    if (!address) return;

    const encodedAddress = encodeURIComponent(address);
    const isMobile =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    let mapsUrl;
    if (isMobile && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      // iOS - try Apple Maps first, fallback to Google Maps
      mapsUrl = `maps://maps.apple.com/?q=${encodedAddress}`;
    } else if (isMobile && /Android/i.test(navigator.userAgent)) {
      // Android - use Google Maps
      mapsUrl = `geo:0,0?q=${encodedAddress}`;
    } else {
      // Desktop - use Google Maps web
      mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }

    window.open(mapsUrl, "_blank");
  }

  // Loading methods removed - no loading UI needed

  // Error display removed - not needed

  // Caching helper methods
  generateLocationCacheKey(location) {
    // Round to ~100m precision for caching
    const lat = Math.round(location.latitude * 1000) / 1000;
    const lon = Math.round(location.longitude * 1000) / 1000;
    return `${lat},${lon}`;
  }

  hasLocationChanged(newLocation) {
    if (!this.userLocation) return true;

    const distance = this.calculateHaversineDistance(
      this.userLocation.latitude,
      this.userLocation.longitude,
      newLocation.latitude,
      newLocation.longitude
    );

    // Consider location changed if moved more than 100m
    return distance > 0.1;
  }

  hasCachedDistances() {
    if (!this.locationCacheKey) return false;

    const cached = this.distanceCache.get(this.locationCacheKey);
    if (!cached) return false;

    // Check if cache is still valid (not expired)
    const isExpired = Date.now() - cached.timestamp > this.cacheExpiryTime;
    if (isExpired) {
      this.distanceCache.delete(this.locationCacheKey);
      return false;
    }

    return true;
  }

  loadCachedDistances() {
    const cached = this.distanceCache.get(this.locationCacheKey);
    if (!cached) return;

    this.storeCards.forEach((store) => {
      const storeKey = `${store.latitude},${store.longitude}`;
      store.distance = cached.distances.get(storeKey) || null;
    });

    this.lastCalculationTime = cached.timestamp;
  }

  cacheDistances() {
    if (!this.locationCacheKey) return;

    const distances = new Map();
    this.storeCards.forEach((store) => {
      if (store.distance !== null) {
        const storeKey = `${store.latitude},${store.longitude}`;
        distances.set(storeKey, store.distance);
      }
    });

    this.distanceCache.set(this.locationCacheKey, {
      distances: distances,
      timestamp: Date.now(),
    });

    // Limit cache size to prevent memory issues
    if (this.distanceCache.size > 10) {
      const oldestKey = this.distanceCache.keys().next().value;
      this.distanceCache.delete(oldestKey);
    }
  }

  // Efficient state management
  shouldRecalculateDistances() {
    if (!this.lastCalculationTime) return true;
    if (!this.userLocation) return false;

    const timeSinceLastCalculation = Date.now() - this.lastCalculationTime;
    return timeSinceLastCalculation > this.cacheExpiryTime;
  }

  clearCache() {
    this.distanceCache.clear();
    this.locationCacheKey = null;
    this.lastCalculationTime = null;
  }

  // Smooth scrolling optimizations
  setupSmoothScrolling() {
    if (!this.storesList) return;

    // Add momentum scrolling for iOS
    this.storesList.style.webkitOverflowScrolling = "touch";

    // Optimize scroll performance
    let scrollTimeout;
    let isScrolling = false;

    this.storesList.addEventListener(
      "scroll",
      () => {
        if (!isScrolling) {
          isScrolling = true;
          this.storesList.style.pointerEvents = "none";
        }

        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          isScrolling = false;
          this.storesList.style.pointerEvents = "auto";
        }, 150);
      },
      { passive: true }
    );

    // Add keyboard navigation for smooth scrolling
    this.storesList.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        this.handleKeyboardScroll(e.key);
      }
    });
  }

  handleKeyboardScroll(direction) {
    const cardWidth =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--nearby-stores-card-width"
        )
      ) || 280;
    const gap =
      parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--nearby-stores-gap")
          .replace("rem", "")
      ) * 16 || 16;

    const scrollAmount = cardWidth + gap;
    const currentScroll = this.storesList.scrollLeft;
    const targetScroll =
      direction === "ArrowRight"
        ? currentScroll + scrollAmount
        : currentScroll - scrollAmount;

    this.smoothScrollTo(targetScroll);
  }

  smoothScrollTo(targetPosition) {
    const startPosition = this.storesList.scrollLeft;
    const distance = targetPosition - startPosition;
    const duration = 300;
    let startTime = null;

    const animateScroll = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function for smooth animation
      const easeInOutCubic =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      this.storesList.scrollLeft = startPosition + distance * easeInOutCubic;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  optimizeScrollPosition() {
    // Force scroll position to absolute start
    this.storesList.scrollLeft = 0;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.storesList.scrollLeft = 0;
    });
  }

  setupResponsiveCards() {
    // Calculate and set card width directly
    this.updateCardWidth();

    // Update on resize
    const updateCardCounts = () => {
      this.updateCardWidth();
    };

    window.addEventListener("resize", updateCardCounts, { passive: true });

    // Add observer to ensure scroll position is correct when content changes
    if (this.storesList) {
      const observer = new MutationObserver(() => {
        // Reset scroll position whenever content changes
        setTimeout(() => {
          this.storesList.scrollLeft = 0;
        }, 50);
      });

      observer.observe(this.storesList, {
        childList: true,
        subtree: true,
      });
    }
  }

  updateCardWidth() {
    if (!this.storesList) return;

    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
    
    // Determine cards count based on screen size and settings
    let cardsCount;
    if (isMobile) {
      cardsCount = this.cardsMobile;
    } else {
      cardsCount = this.cardsDesktop;
      // Adjust for larger screens if not explicitly set
      if (window.innerWidth >= 1600 && this.cardsDesktop <= 4) {
        cardsCount = Math.min(5, this.cardsDesktop + 1);
      }
      if (window.innerWidth >= 1920 && this.cardsDesktop <= 5) {
        cardsCount = Math.min(6, this.cardsDesktop + 2);
      }
    }
    
    // Get actual gap from CSS custom property
    const gapRem = parseFloat(getComputedStyle(document.documentElement)
      .getPropertyValue('--nearby-stores-gap').replace('rem', '')) || 1.5;
    const gap = gapRem * 16; // Convert rem to pixels
    
    // Get container padding from CSS
    const containerStyle = getComputedStyle(this.storesList);
    const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(containerStyle.paddingRight) || 0;
    const containerPadding = paddingLeft + paddingRight;
    
    // Calculate available width using the parent container width
    const parentContainer = this.storesList.closest('.nearby-stores-container') || this.storesList.parentElement;
    const containerWidth = parentContainer.offsetWidth || parentContainer.clientWidth;
    const availableWidth = containerWidth - containerPadding;
    const totalGaps = Math.max(0, (cardsCount - 1) * gap);
    
    // Calculate card width with minimum constraints
    const minCardWidth = isMobile ? 240 : 280;
    const maxCardWidth = isMobile ? 320 : 400;
    let cardWidth = (availableWidth - totalGaps) / cardsCount;
    
    // Apply constraints
    cardWidth = Math.max(minCardWidth, Math.min(maxCardWidth, cardWidth));
    
    // Set the card width directly
    this.storesList.style.setProperty('--card-width', `${Math.floor(cardWidth)}px`);
    
    console.log(`Screen: ${window.innerWidth}px, Cards: ${cardsCount}, Container: ${containerWidth}px, Available: ${availableWidth}px, Card width: ${Math.floor(cardWidth)}px`);
  }
}

// Simple initialization
document.addEventListener("DOMContentLoaded", function () {
  const containers = document.querySelectorAll(".nearby-stores-container");
  containers.forEach(function (container) {
    new NearbyStores(container);
  });
});

// Fallback for when script loads after DOM is ready
if (document.readyState !== "loading") {
  const containers = document.querySelectorAll(".nearby-stores-container");
  containers.forEach(function (container) {
    new NearbyStores(container);
  });
}
